/**
 * Imports necessary types and functions from various modules
 */
import type {
  AttributeParams,
  BehaviorParams,
  Campaign,
  CollectionResult,
  CollectionSearchResult,
  CustomBlogSearchData,
  CustomBlogSearchParameters,
  Locale,
  PaginationParams,
  Params,
  Product,
  ProductVariant,
  QueryParams,
  Result,
  CommerceContext as Context
} from '@agapi/mlc-core';

import type { Loop54ClientConfig } from './loop54/client';
import type { Loop54CustomDataBlogResponse, EntityCollection, Event } from './loop54/types';

import { CommerceAdapter } from '@agapi/mlc-core';

import {
  createEvents,
  getBasketRecommendations,
  getEntities,
  getEntitiesByAttribute,
  getPopularEntities,
  getRecentEntities,
  getRecommendedEntities,
  getRelatedEntities,
} from './loop54';

import {
  transformCollectionResult,
  transformItems,
  transformProductVariant,
  transformProductVariants,
  transformProducts,
  transformBlogPosts,
  transformCategories,
  transformBrands,
} from './transformer';

import { getBaseFilter, getFacets } from './utils/general';
import { doSearch } from './utils/calls';

/**
 * CommerceLoop54Adapter class that extends CommerceAdapter
 */
export class CommerceLoop54Adapter extends CommerceAdapter<Loop54ClientConfig> {
  constructor(config: Loop54ClientConfig) {
    super(config);
    config.marketIds = Object.fromEntries(Object.entries(config.marketIds).filter(([ _, v ]) => v.trim()));
  }

  /**
   * Retrieves products based on given parameters
   * @param params - The attribute parameters for the query
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a collection result of products
   */
  async getProducts(params: AttributeParams, locale: Locale, context: Context): Promise<CollectionResult<Product>> {
    // TODO: fix this later on somewhere else, this seems to be an issue with pathnames when using ISR
    const removeTrailingSlashes = (value: string | number | boolean) => typeof value === 'string' ? value.replace(/\/$/, '') : value;
    params.attribute.value = Array.isArray(params.attribute.value) ? params.attribute.value.map((v) => removeTrailingSlashes(v)) : removeTrailingSlashes(params.attribute.value);

    console.log('[CommerceLoop54Adapter.getProducts()]:', params, context);

    try {
      const response = await getEntitiesByAttribute(
        this.config.fetch,
        locale,
        {
          attribute: {
            name: params.attribute.name,
            value: (params.attribute.value as string).replace(/\/$/, '') ?? []
          },
          resultsOptions: {
            ...getBaseFilter(params, locale, this.config),
            ...getFacets(params),
            take: params.limit,
            skip: params.offset,
            sortBy: params.sortBy,
          },
          userId: context.userId,
          config: { ...this.config, locale }
        }
      );
      return transformCollectionResult(response, (response) => transformProducts(transformItems(response.results.items), locale, this.config), params);
    } catch (e) {
      console.error(e);
      return { items: [], count: 0, facetFilters: [], limit: params.limit, offset: 0 };
    }
  }

  /**
   * Retrieves product variants based on given parameters
   * @param params - The parameters for the query
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a collection result of product variants
   */
  async getProductVariants(params: Params, locale: Locale, context: Context): Promise<CollectionResult<ProductVariant>> {
    return { items: [], count: 0, facetFilters: [], offset: 0 };
  }

  /**
   * Retrieves a product by its handle
   * @param handle - The handle of the product
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a result containing the product
   */
  async getProduct(handle: string, locale: Locale, context: { userId: string }): Promise<Result<Product>> {
    // console.log('[CommerceLoop54Adapter] getProduct', handle);
    // console.log('[CommerceLoop54Adapter] getProduct', context);
    // console.log('[CommerceLoop54Adapter] getProduct', this.config);
    try {
      const response = await getEntities(
        this.config.fetch,
        locale,
        {
          resultsOptions: {
            filter: { type: 'attribute', attributeName: 'handle', value: handle }
          },
          userId: context.userId,
          config: { ...this.config, locale }
        }
      );

      const item = transformProducts(transformItems(response.results.items), locale, this.config)[0];

      return { item };
    } catch (e) {
      return {}; // Return undefined/null instead?
    }
  }

  /**
   * Retrieves a product variant by its SKU
   * @param sku - The SKU of the product variant
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a result containing the product variant
   */
  async getProductVariant(sku: string, locale: Locale, context: { userId: string }): Promise<Result<ProductVariant>> {
    try {
      const response = await getEntities(
        this.config.fetch,
        locale,
        {
          resultsOptions: {
            filter: { type: 'attribute', attributeName: 'sku', value: sku }
          },
          userId: context.userId,
          config: { ...this.config, locale }
        }
      );
      return {
        item: transformProductVariant(transformItems(response.results.items)[0], locale, this.config)
      };
    } catch (e) {
      return {}; // Return undefined/null instead?
    }
  }

  /**
   * Retrieves active campaigns
   * @returns A promise that resolves to an array of campaigns
   */
  async getActiveCampaigns(): Promise<Campaign[]> {
    return [];
  }

  /**
   * Performs a search based on given parameters
   * @param params - The query parameters for the search
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a collection search result of product variants
   */
  async search(params: QueryParams, locale: Locale, context: Context): Promise<CollectionSearchResult<ProductVariant>> {
    try {
      const mappedResult = await doSearch(this.config, params, locale, context, {
        directSearch: params.directSearch ? true : false
      });

      return {
        ...transformCollectionResult(mappedResult.search, (response) => transformProductVariants(transformItems(response.results.items), locale, this.config), params),
        query: params.query,
        autoComplete: mappedResult.autoComplete.queries.items.map((i) => i.query),
      };
    } catch (e) {
      return { query: params.query, items: [], count: 0, offset: 0, facetFilters: [], autoComplete: [] };
    }
  }

  async getPopularSearches(params: PaginationParams, locale: Locale, context: Context): Promise<CollectionResult<string>> {
    const response = await getPopularEntities(
      this.config.fetch,
      locale,
      {
        behaviorType: 'search',
        entityType: ['Query'],
        resultsOptions: {
          skip: params.offset ?? 0,
          take: params.limit ?? 10
        },
        userId: context.userId,
        config: { ...this.config, locale }
      }
    );

    return {
      items: response.results.items.map((i) => i.id),
      count: response.results.count,
      offset: params.offset ?? 0,
      limit: params.limit ?? 10,
      facetFilters: [],
    };
  }

  /**
   * Retrieves recommendations based on given parameters
   * @param params - The parameters for the recommendation query
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a collection result of product variants
   */
  async getRecommendation(params: Params, locale: Locale, context: Context): Promise<CollectionResult<ProductVariant>> {
    console.log('getRecommendation-loop54', params);

    params.filter = [
      ...(params.filter ?? []),
      { type: 'attribute', attributeName: 'stock', value: 0, comparisonMode: 'greaterThan' }
    ];

    const response = await getRecommendedEntities(
      this.config.fetch,
      locale,
      {
        resultsOptions: {
          take: params.limit,
          skip: params.offset,
          sortBy: params.sortBy,
          ...getBaseFilter(params, locale, this.config),
        },
        userId: context.userId,
        config: { ...this.config, locale }
      }
    );
    return transformCollectionResult(response, (response) => transformProductVariants(transformItems(response.results.items), locale, this.config), params);
  };

  /**
   * Retrieves recommendations derived from given SKUs
   * @param skus - An array of SKUs to derive recommendations from
   * @param params - The parameters for the recommendation query
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a collection result of product variants
   */
  async getRecommendationDerivedFrom(skus: string[], params: Params, locale: Locale, context: Context): Promise<CollectionResult<ProductVariant>> {
    if(!skus.length) {
      return { items: [], count: 0, facetFilters: [], offset: 0 };
    }

    params.filter = [
      ...(params.filter ?? []),
      { type: 'attribute', attributeName: 'stock', value: 0, comparisonMode: 'greaterThan' }
    ];

    const response = await getBasketRecommendations(
      this.config.fetch,
      locale,
      {
        entities: skus.map((sku) => ({ id: sku, type: 'Product' })),
        resultsOptions: {
          take: params.limit,
          skip: params.offset,
          sortBy: params.sortBy,
          ...getBaseFilter(params, locale, this.config),
        },
        userId: context.userId,
        config: { ...this.config, locale }
      }
    );
    console.log('getRecommendationDerivedFrom-loop54', response);
    return transformCollectionResult(response, (response) => transformProductVariants(transformItems(response.results.items), locale, this.config), params);
  }

  /**
   * Retrieves related products for a given SKU
   * @param sku - The SKU to find related products for
   * @param params - The parameters for the query
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a collection result of product variants
   */
  async getRelatedProducts(sku: string, params: Params, locale: Locale, context: Context): Promise<CollectionResult<ProductVariant>> {
    params.filter = [
      ...(params.filter ?? []),
      { type: 'attribute', attributeName: 'stock', value: 0, comparisonMode: 'greaterThan' }
    ];

    const response = await getRelatedEntities(
      this.config.fetch,
      locale,
      {
        resultsOptions: {
          take: params.limit,
          skip: params.offset,
          sortBy: params.sortBy,
          ...getBaseFilter(params, locale, this.config),
        },
        entity: { type: 'Product', id: sku },
        userId: context.userId,
        config: { ...this.config, locale }
      }
    );
    return transformCollectionResult(response, (response) => transformProductVariants(transformItems(response.results.items), locale, this.config), params);
  }

  /**
   * Retrieves popular products based on given parameters
   * @param params - The behavior parameters for the query
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a collection result of product variants
   */
  async getPopularProducts(params: BehaviorParams, locale: Locale, context: Context): Promise<CollectionResult<ProductVariant>> {
    params.filter = [
      ...(params.filter ?? []),
      { type: 'attribute', attributeName: 'stock', value: 0, comparisonMode: 'greaterThan' }
    ];

    const response = await getPopularEntities(
      this.config.fetch,
      locale,
      {
        behaviorType: params.behaviorType,
        resultsOptions: {
          take: params.limit,
          skip: params.offset,
          sortBy: params.sortBy,
          ...getBaseFilter(params, locale, this.config),
        },
        userId: context.userId,
        config: { ...this.config, locale }
      }
    );
    return transformCollectionResult(response, (response) => transformProductVariants(transformItems(response.results.items), locale, this.config), params);
  }

  /**
   * Retrieves recently interacted products based on given parameters
   * @param params - The behavior parameters for the query
   * @param context - The context containing the user ID
   * @returns A promise that resolves to a collection result of product variants
   */
  async getRecentlyInteractedProducts(params: BehaviorParams, locale: Locale, context: Context): Promise<CollectionResult<ProductVariant>> {
    const response = await getRecentEntities(
      this.config.fetch,
      locale,
      {
        behaviorType: params.behaviorType,
        resultsOptions: {
          take: params.limit,
          skip: params.offset,
          sortBy: params.sortBy,
          ...getBaseFilter(params, locale, this.config),
        },
        userId: context.userId,
        config: { ...this.config, locale }
      }
    );
    return transformCollectionResult(response, (response) => transformProductVariants(transformItems(response.results.items), locale, this.config), params);
  }

  /**
   * Records events
   * @param events - An array of event objects
   * @param context - The context containing the user ID
   * @returns A promise that resolves when the events are created
   */
  async events(events: { type: string; sku: string; name?: string; quantity?: number; orderId?: string; revenue?: number; }[], locale: Locale, context: { userId: string }): Promise<void> {
   const params = {
      events: events.map<Event>((e) => {
        return {
          type: e.type,
          entity: {
            type: 'Product',
            id: e.sku,
            attributes: e.name ? [ {
              type: 'string',
              name: 'title',
              values: [ e.name ]
            } ] : undefined
          },
          revenue: e.revenue,  // Used for purchase events
          orderId: e.orderId,  // Used for purchase events
          quantity: e.quantity // Used for purchase events
        };
      }),
      userId: context.userId,
      config: this.config
    };

    await createEvents(this.config.fetch, locale, params);
  }

  async getSitemap(pagination: PaginationParams, locale: Locale, _context: Context): Promise<EntityCollection> {
    const response = await getEntities(this.config.fetch, locale, {
      userId: 'All-Fetcher',
      customData: {
        requestType: 'SiteMap'
      },
      resultsOptions: {
        take: pagination.limit,
        skip: pagination.offset,
      },
      config: this.config
    });

    return response.results;
  }

  async getPaginatedProducts(pagination: PaginationParams, locale: Locale, _context: Context): Promise<CollectionResult<ProductVariant>> {
    const response = await getEntities(this.config.fetch, locale, {
      userId: 'Internal-Fetcher',
      resultsOptions: {
        take: pagination.limit,
        skip: pagination.offset,
      },
      config: this.config
    });

    return transformCollectionResult(response, (response) => transformProductVariants(transformItems(response.results.items), locale, this.config), {
      limit: pagination.limit,
      offset: pagination.offset,
    });
  }
}

/**
 * CommerceBlogLoop54Adapter class that extends CommerceLoop54Adapter and implements the search method with blog results
 */
export class CommerceSupportingBlogLoop54Adapter extends CommerceLoop54Adapter {
  constructor(config: Loop54ClientConfig) {
    super(config);
  }

  async search(params: QueryParams<CustomBlogSearchParameters>, locale: Locale, context: Context): Promise<CollectionSearchResult<ProductVariant, CustomBlogSearchData>> {
    try {
        const mappedResult = await doSearch<CustomBlogSearchParameters, Loop54CustomDataBlogResponse>(this.config, params, locale, context, {
        directSearch: params.directSearch ? true : false,
        ...(params.includeBlogResults && {
          blogResultsOptions: { skip: 0, take: params.blogResultsLimit ?? 5 }
        }),
        ...(params.includeCategoryResults && {
          categoryResultsOptions: { skip: 0, take: params.categoryResultsLimit ?? 5 }
        }),
        ...(params.includeBrandResults && {
          brandResultsOptions: { skip: 0, take: params.brandResultsLimit ?? 5 }
        })
      });

      return {
        ...transformCollectionResult(mappedResult.search, (response) => transformProductVariants(transformItems(response.results.items), locale, this.config), params),
        query: params.query,
        autoComplete: mappedResult.autoComplete.queries.items.map((i) => i.query),
        blogResults: transformBlogPosts(mappedResult.search.customData?.blogResults?.results.items ?? []),
        categoryResults: transformCategories(mappedResult.search.customData?.categoryResults?.results.items ?? []),
        brandResults: transformBrands(mappedResult.search.customData?.brandResults?.results.items ?? []),
      };
    } catch (e) {
      return { query: params.query, items: [], count: 0, offset: 0, facetFilters: [], autoComplete: [] };
    }
  }
}
